import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CrudService } from '../crud.service';
import { CreateCredentialsDialogComponent } from '../dialogs/create-credentials-dialog/create-credentials-dialog.component';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, startWith, filter, defaultIfEmpty } from 'rxjs/operators';
import { Credentials } from '../../shared/classes/credentials';
import { CredentialsStatus } from '../../shared/enums/credentials-status.enum';
import { Certificate } from '../../shared/classes/certificate';
import { Enterprise } from '../../shared/classes/enterprise';
import { LoginService } from '../../shared/services/login.service';
import { Application } from '../../shared/classes/application';
import { Client } from '../../shared/classes/client';
import { User } from '../../shared/classes/user';
import { AccessGroup } from '../../shared/classes/access-group';
import { AccessPolicy } from '../../shared/classes/access-policy';
import { HashAlgorithm } from '../../shared/enums/hash-algorithm.enum';
import { OpenIdService } from '../openid.service';
import { Domain } from '../../shared/classes/domain';
import { CreateDomainDialogComponent } from '../dialogs/create-domain-dialog/create-domain-dialog.component';
import { VerifyDomainDialogComponent, VerifyDomainDialogConfig, VerifyDomainDialogOutcome } from '../dialogs/verify-domain-dialog/verify-domain-dialog.component';
import { DomainStatus } from '../../shared/enums/domain-status.enum';
import { Directory } from '../../shared/classes/directory';
import { DirectoryType } from '../../shared/enums/directory-type.enum';
import { DomainVerificationType } from '../../shared/enums/domain-verification-type.enum';
import { DefaultDialogConfig } from '../../shared/classes/default-dialog-config';
import {
  AlertMessageDialogConfig,
  DangerConfirmationDialogConfig,
  MessageDialogComponent} from '../dialogs/message-dialog/message-dialog.component';
import { Invitation } from '../../shared/classes/invitation';
import { DomainRegistrationUrl } from '../../shared/classes/domain-registration-url';
import { SnackbarService } from '../../shared/services/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  private readonly crudService = inject(CrudService);
  private readonly openIdService = inject(OpenIdService);
  private readonly matDialog = inject(MatDialog);
  private readonly loginService = inject(LoginService);
  private readonly snackbar = inject(SnackbarService);


  private get user(): User {
    return this.loginService.user;
  }

  public getAccessGroups(): Observable<AccessGroup[]> {
    return this.crudService.getAccessGroups(this.user.enterpriseId);
  }

  public getAccessPoliciesForApplication(applicationId: string): Observable<AccessPolicy[]> {
    return this.crudService.getAccessPoliciesForApplication(this.user.enterpriseId, applicationId);
  }

  public getAccessPoliciesForGroup(accessGroupId: string): Observable<AccessPolicy[]> {
    return this.crudService.getAccessPoliciesForGroup(this.user.enterpriseId, accessGroupId);
  }

  public getCertificates(): Observable<Certificate[]> {
    return this.crudService.getCertificates();
  }

  public getCredentials(): Observable<Credentials[]> {
    return this.crudService.getCredentials().pipe(
      tap(credentials => credentials.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime())),
    );
  }

  public getClient(applicationId: string, clientId: string): Observable<Client> {
    return this.crudService.getClient(this.user.enterpriseId, applicationId, clientId);
  }

  public getClientMetadata(idpEntityId: string): Observable<any> {
    return this.crudService.getClientMetadata(idpEntityId);
  }

  public getClientPowerShell(idpEntityId: string): Observable<any> {
    return this.crudService.getPowerShellScript(idpEntityId);
  }

  public getClientFingerprint(entityId: string, hashType: HashAlgorithm): Observable<string> {
    return this.openIdService.getFingerprint(entityId, hashType);
  }

  public getEnterpriseList(): Observable<Enterprise[]> {
    return this.crudService.getEnterprises();
  }

  public getEnterprise(): Observable<Enterprise> {
    return this.crudService.getEnterprise(this.user.enterpriseId);
  }

  public getApplicationList(): Observable<Application[]> {
    return this.crudService.getApplications(this.user.enterpriseId);
  }

  public getDomains(): Observable<Domain[]> {
    return this.crudService.getDomains(this.user.enterpriseId);
  }

  public getApprovedDomains(types?: DomainVerificationType[]): Observable<string[]> {
    return this.crudService.getApprovedDomains(this.user.enterpriseId, types);
  }

  public getInvitation(invitationId: string): Observable<Invitation> {
    return this.crudService.getInvitation(this.user.enterpriseId, invitationId);
  }

  public getInvitations(): Observable<Invitation[]> {
    return this.crudService.getInvitations(this.user.enterpriseId);
  }

  public updateDomainRegistrationUrl(registrationUrl: DomainRegistrationUrl): Observable<void> {
    return this.crudService.updateDomainRegistrationUrl(this.user.enterpriseId, registrationUrl);
  }

  public createRegistrationUrlInvites(registrationUrl: DomainRegistrationUrl, emails: string[]): Observable<void> {
    return this.crudService.createRegistrationUrlInvites(this.user.enterpriseId, registrationUrl, emails);
  }

  //#region Directory
  public createDirectory(directory: Directory): Observable<Directory> {
    return this.crudService.createDirectory(this.user.enterpriseId, directory);
  }

  public getDirectoryList(type: DirectoryType): Observable<Directory[]> {
    return this.crudService.getDirectories(this.user.enterpriseId, type);
  }

  public getDirectory(directoryId: string): Observable<Directory> {
    return this.crudService.getDirectory(this.user.enterpriseId, directoryId);
  }

  public getDirectoryAccessGroups(directoryId: string): Observable<AccessGroup[]> {
    return this.crudService.getDirectoryAccessGroups(this.user.enterpriseId, directoryId);
  }

  public updateDirectory(directoryId: string, directory: Directory): Observable<void> {
    return this.crudService.updateDirectory(this.user.enterpriseId, directoryId, directory);
  }

  public deleteDirectory(directoryId: string): Observable<void> {
    return this.crudService.deleteDirectory(this.user.enterpriseId, directoryId);
  }
  //#endregion Directory

  public createAccessPolicy(accessPolicy: AccessPolicy): Observable<void> {
    return this.crudService.createAccessPolicy(this.user.enterpriseId, accessPolicy);
  }

  /**
   * Open a dialog to prompt the user to add credentails.
   * @returns Whether data has changed.
   */
  public createCredentials(): Observable<boolean> {
    return this.matDialog.open(CreateCredentialsDialogComponent, new DefaultDialogConfig({ disableClose: true })).afterClosed();
  }

  public createClient(applicationId: string, client: Client): Observable<Client> {
    return this.crudService.createClient(this.user.enterpriseId, applicationId, client);
  }

  public createApplicationUsers(regUrl: string, emails: string[]): Observable<void> {
    return this.crudService.createApplicationUsers(this.user.enterpriseId, regUrl, emails);
  }

  public updateApplication(application: Application): Observable<void> {
    return this.crudService.updateApplication(this.user.enterpriseId, application);
  }

  public updateClient(applicationId: string, client: Client): Observable<void> {
    return this.crudService.updateClient(this.user.enterpriseId, applicationId, client);
  }

  public refreshClientSecret(applicationId: string, clientId: string): Observable<Client> {
    return this.crudService.refreshClientSecret(this.user.enterpriseId, applicationId, clientId);
  }

  public deleteAccessPolicy(accessPolicy: AccessPolicy): Observable<void> {
    return this.crudService.deleteAccessPolicy(this.user.enterpriseId, accessPolicy);
  }

  public deleteClient(applicationId: string, clientId: string): Observable<void> {
    return this.crudService.deleteClient(this.user.enterpriseId, applicationId, clientId);
  }

  /**
   * Opens a dialog informing the user they're at their credentials limit and cannot add more.
   */
  public openCredentialLimitDialog(): Observable<any> {
    const dialogConfig = new AlertMessageDialogConfig(
      'Maximum number of credentials have been reached. Please delete an entry before adding more.');
    return this.matDialog.open(MessageDialogComponent, dialogConfig).afterClosed();
  }

  /**
   * Opens a domain creation dialog.
   * @returns Whether data has changed.
   */
  public createDomain(): Observable<boolean> {
    return this.matDialog.open(CreateDomainDialogComponent, new DefaultDialogConfig({ disableClose: true })).afterClosed().pipe(
      switchMap(domain => {
        if (!!domain) {
          if (domain.status === DomainStatus.Pending) {
            return this.verifyDomain(domain).pipe(startWith(true));
          }
          return of(true);
        }
        return of(false);
      }),
    );
  }

  /**
   * Opens a domain verification dialog.
   * @returns Whether data has changed.
   */
  public verifyDomain(domain: Domain): Observable<boolean> {
    return this.matDialog.open(VerifyDomainDialogComponent, new VerifyDomainDialogConfig(domain)).afterClosed().pipe(
      switchMap((outcome: VerifyDomainDialogOutcome) => {
        if (outcome === VerifyDomainDialogOutcome.DnsSuccess) {
          this.snackbar.showInfoBanner('DNS verification is successfully completed.');
        }
        return of(true);
      }),
    );
  }

  /**
   * Delete a credentials, domain or invitation resource.
   * @param resource The credentials, domain or invitiaton resource to delete.
   * @returns Whether data has changed as a result of this interaction.
   */
  public deleteResource(resource: Credentials | Domain | Invitation): Observable<boolean> {
    const dialogConfig = new DangerConfirmationDialogConfig(
      'Are you sure you want to delete this resource?', 'Delete');
    return this.matDialog.open(MessageDialogComponent, dialogConfig).afterClosed().pipe(
      filter(Boolean),
      switchMap(_ => {
        if (resource instanceof Credentials) {
          resource.status = CredentialsStatus.Disabled;
          return this.crudService.updateCredentials(resource);
        } else if (resource instanceof Domain) {
          const enterpriseId = this.user.enterpriseId || resource.enterpriseId;
          return this.crudService.deleteDomain(enterpriseId, resource.id);
        } else if (resource instanceof Invitation) {
          return this.crudService.deleteInvitation(this.user.enterpriseId, resource.id);
        }
      }),
      // crud service calls emit null then complete, map null to true
      map(_ => true),
      defaultIfEmpty(false)
    );
  }
}
